import { Component, EventEmitter, forwardRef, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormControl, NG_VALUE_ACCESSOR, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { AbilityService } from 'ability';
import { I18NextService } from 'angular-i18next';
import { FormsUtil, MbsSize, MbsValidators, ModalService, ModalSettings } from 'mbs-ui-kit';
import { stopAfterMinutesValidator } from 'mbs-ui-shared@utils/validators';
import moment from 'moment/moment';
import { BehaviorSubject, noop } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { StepsHelpers } from 'wizards/helpers/steps-helpers';
import { FullBackupData, IncrementalData, ScheduleStepValue, ScheduleType } from 'wizards/models/schedule-models';
import { WeekNumber } from 'wizards/models/WeekNumber.enum';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { NBFAdditionalModalComponent } from 'wizards/steps/schedule-step/NBF/additional-modal/additional-modal.component';
import { NBFScheduleModalComponent } from 'wizards/steps/schedule-step/NBF/schedule-modal/schedule-modal.component';
import { ScheduleStepBase } from 'wizards/steps/Schedule.StepBase.class';
import {
  daysOfWeekEnumArray,
  getDefaultFullSchedule,
  getDefaultIncrementalSchedule,
  isSelectedDaysValidator,
  weekNumberEnumArray
} from './schedule-step.component.utils';

const NBFScheduleStepValueAccessor: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => NBFScheduleStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-nbf-schedule-step',
  templateUrl: './schedule-step.component.html',
  styleUrls: ['./schedule-step.component.scss'],
  providers: [NBFScheduleStepValueAccessor]
})
export class NBFScheduleStepComponent extends ScheduleStepBase<ScheduleStepValue> implements OnInit {
  @Output() changeType = new EventEmitter<keyof typeof ScheduleType>();

  public isGFSMode: boolean;
  public invalidScheduleTypeSelection = false;
  public readonly elementSelectors = {
    id: {
      rootDiv: 'NBF-Schedule-root-div',
      defaultModeBlock: 'NBF-Schedule-default-mode-block',
      oldAgentForFFIAlert: 'NBF-Schedule-old-agent-for-FFI-alert',
      invalidFFIAlert: 'NBF-Schedule-invalid-FFI-alert',
      tileSimple: 'NBF-Schedule-tile-simple',
      tileAdvanced: 'NBF-Schedule-tile-advanced',
      noSchedule: 'NBF-Schedule-checkbox-no-schedule',
      simpleModeBlock: 'NBF-Schedule-simple-mode-block',
      buttonSimple: 'NBF-Schedule-button-simple',
      advancedModeBlock: 'NBF-Schedule-advanced-mode-block',
      switcherAdvanced: 'NBF-Schedule-switcher-advanced',
      switcherAdvancedFull: 'NBF-Schedule-switcher-advanced-full',
      buttonAdvanced: 'NBF-Schedule-button-advanced',
      buttonFull: 'NBF-Schedule-button-full',
      alertFull: 'NBF-Schedule-alert-full',
      additionalModeBlock: 'NBF-Schedule-additional-mode-block'
    }
  };

  public scheduleInfo: { simple: string; incremental: string; full: string } = {
    simple: '',
    incremental: '',
    full: ''
  };

  public canOffFull: boolean;

  public get notConfiguredFull(): boolean {
    if (this.canOffFull) {
      return false;
    }

    return this.isAdvancedMode && !this.stepForm.get('fullBackupData').get('enabled').value;
  }

  public get isSimpleMode(): boolean {
    return this.stepForm?.get('ScheduleType').value === 'ffi';
  }

  public get isManualMode(): boolean {
    return this.stepForm?.get('ScheduleType').value === 'noschedule';
  }

  public get isAdvancedMode(): boolean {
    return this.stepForm?.get('ScheduleType').value === 'predefined';
  }

  private $deactivateSimple = new BehaviorSubject<boolean>(false);
  private daysShort = this.i18nextService.t('shared:daysShort', { returnObjects: true });
  private initialScheduleType: string;
  private isFFIConfirmed = false;

  @ViewChild('FFIPayAttentionGFS', { static: true, read: TemplateRef }) FFIPayAttentionGFS: TemplateRef<any>;

  constructor(
    private modalService: ModalService,
    public ability: AbilityService,
    public mainService: RemoteManagementWizardsService,
    protected stepService: WizardStepsService,
    protected i18nextService: I18NextService
  ) {
    super(mainService, stepService, i18nextService);

    this.canOffFull = ability.can('read', 'AllowNbfScheduleWithoutFullBackups');
  }

  ngOnInit(): void {
    this.initForm();
    this.initEvents();
  }

  initForm(): void {
    super.initForm();

    this.stepForm = new UntypedFormGroup(
      {
        ScheduleType: new FormControl(''),
        ScheduleTypeNone: new FormControl(false), // Virtual property
        specificDateGroup: new UntypedFormGroup({
          date: new FormControl(),
          time: new FormControl('00:00', MbsValidators.timeValidatorWithoutText)
        }),
        StopAfterEnabled: new FormControl(false),
        stopAfterHours: new FormControl(0),
        stopAfterMinutes: new FormControl(0, stopAfterMinutesValidator.bind(this, 'stopAfterHours')),
        ForceMissedSchedule: new FormControl(false),
        overdueAfterSuccessEnabled: new FormControl(false),
        overdueAfterSuccessAmount: new FormControl(1, Validators.min(1)),
        overdueAfterSuccessPeriod: new FormControl(0),
        incrementalData: new UntypedFormGroup(getDefaultIncrementalSchedule()),
        fullBackupData: new UntypedFormGroup(getDefaultFullSchedule())
      },
      this.isSelectedDaysValidator.bind(this)
    );

    this.initFormEvents();
  }

  onStepFormChange(value: ScheduleStepValue): void {
    const scheduleType = this.stepForm.get('ScheduleType').value;
    const badStorage = scheduleType === 'ffi' && this.hasFFIProblem;
    const correctSchedule = !this.notConfiguredFull && !scheduleType;
    const scheduleSelected = !!scheduleType;
    const needConfirmFFI = this.getNeedConfirmFFI();

    this.value = {
      ...value,
      valid: this.stepForm.valid && !badStorage && !correctSchedule && scheduleSelected && !this.notConfiguredFull && !needConfirmFFI
    };

    this.updateScheduleInfo();
    this.stepService.updateProperty('schedule', 'isSimpleMode', this.isSimpleMode);
    this.stepService.updateProperty('schedule', 'isAdvancedMode', this.isAdvancedMode);
    this.stepService.updateProperty('schedule', 'isManualMode', this.isManualMode);

    if (this.notConfiguredFull) {
      return void this.addStepMark.emit({
        stepName: this.title,
        actions: { save: { description: this.i18nextService.t('wizards:schedule.advancedMode.fullNotConfiguredInfo'), important: true } }
      });
    }

    this.removeStepMark.emit({ stepName: this.title, actions: ['save'] });
  }

  ngOnChanges(changes: SimpleChanges) {
    queueMicrotask(() => this.updateScheduleInfo());
  }

  updateForm(value: ScheduleStepValue): void {
    this.stepForm.reset(value);
    this.stepForm.get('ScheduleTypeNone').setValue(this.stepForm.get('ScheduleType').value === 'noschedule');

    this.initialScheduleType = value.ScheduleType;
    this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').value);
    this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').value);

    if (this.isEdit) {
      this.checkSchedule();
      this.catchBrokenFFI();
    }

    StepsHelpers.updateDaysOfWeek(value.incrementalData.weeklyDayOfWeek, this.stepForm.get('incrementalData') as UntypedFormGroup);
    StepsHelpers.updateDaysOfWeek(value.fullBackupData.weeklyDayOfWeek, this.stepForm.get('fullBackupData') as UntypedFormGroup);
  }

  forceValid(data: any = null): void {
    const needConfirmFFI = this.getNeedConfirmFFI();

    this.checkSchedule();

    if (needConfirmFFI) {
      this.showGFSAlert(data.isNext, data.isSave);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  activateTile(value: keyof typeof ScheduleType): void {
    if (value === 'ffi' && (this.hasFFIProblem || !this.isFFIEnabled)) {
      return;
    }

    this.stepForm.get('ScheduleType').setValue(value);
    this.stepForm.get('ScheduleTypeNone').setValue(value === 'noschedule');
    this.changeType.emit(value);
    this.invalidScheduleTypeSelection = false;

    if (value === 'ffi') {
      this.stepForm.get('ScheduleTypeNone').setValue(false);
      this.stepForm.get('incrementalData').get('enabled').setValue(true);
      this.stepForm.get('fullBackupData').get('enabled').setValue(false);
      this.provideWeeklyDayOfWeek(['incrementalData']);

      this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').value);
    }

    if (value == 'predefined') {
      this.stepForm.get('ScheduleTypeNone').setValue(false);
      this.stepForm.get('fullBackupData').get('enabled').setValue(true);
      this.provideWeeklyDayOfWeek(['incrementalData', 'fullBackupData']);

      const full = this.stepForm.get('fullBackupData');
      const validVersion = this.isRDMode || this.mainService.validVersionForSchedule;

      if (validVersion && !full.get('repeatEveryCount').value && (!this.isLinux || full.get('recurringPeriod').value === 'Monthly')) {
        full.get('repeatEveryCount').setValue(1);
      }

      this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').value);
    }

    if (value === 'noschedule') {
      this.stepForm.get('incrementalData').get('enabled').setValue(false);
      this.stepForm.get('fullBackupData').get('enabled').setValue(false);
    }
  }

  clickDetails(mode: 'simple' | 'advanced' | 'full'): void {
    let final: Function = noop;
    const settings: ModalSettings = { ...new ModalSettings(), size: MbsSize.md, data: { mode, isUnix: this.isUnix } };

    if (mode === 'simple' || mode === 'advanced') {
      settings.data.schedule = { ...this.stepForm.get('incrementalData').value };

      final = (data) => {
        this.stepForm.get('incrementalData').reset(data);
        this.stepService.updateProperty('schedule', 'incrementalSchedule', this.stepForm.get('incrementalData').value);
      };
    }

    if (mode === 'full') {
      settings.data.schedule = { ...this.stepForm.get('fullBackupData').value };
      settings.data.incrementalSchedule = { ...this.stepForm.get('incrementalData').value };

      final = (data) => {
        this.stepForm.get('fullBackupData').reset({ ...data, enabled: true }); // TODO: Test
        this.stepService.updateProperty('schedule', 'fullSchedule', this.stepForm.get('fullBackupData').value);
      };
    }

    this.modalService
      .openCustom(NBFScheduleModalComponent, settings)
      .then((result: { data: IncrementalData | FullBackupData } | null) => {
        if (!result) {
          return;
        }

        final(result.data);
      })
      .catch(noop);
  }

  incrementalEnabledChangeHandler(): void {
    const value = !this.stepForm.get('incrementalData').get('enabled').value;

    this.stepForm.get('incrementalData').get('enabled').setValue(value);
  }

  fullEnabledChangeHandler(): void {
    const value = !this.stepForm.get('fullBackupData').get('enabled').value;

    this.stepForm.get('fullBackupData').get('enabled').setValue(value);

    if (!value && this.isAdvancedMode) {
      this.stepForm.get('incrementalData').get('enabled').setValue(true);
    }
  }

  clickAdditional(): void {
    const propertiesMap = ['StopAfterEnabled', 'stopAfterHours', 'stopAfterMinutes', 'ForceMissedSchedule'];

    if (!this.isRDMode) {
      propertiesMap.push('overdueAfterSuccessEnabled', 'overdueAfterSuccessAmount', 'overdueAfterSuccessPeriod');
    }

    const settings: ModalSettings = {
      ...new ModalSettings(),
      size: MbsSize.md,
      data: {
        isManualMode: this.isManualMode,
        isRDMode: this.isRDMode,
        startFromDate: this.stepForm.get('incrementalData').get('startFromDate').value
      }
    };

    propertiesMap.forEach((key) => {
      settings.data[key] = this.stepForm.get(key).value;
    });

    this.modalService
      .openCustom(NBFAdditionalModalComponent, settings)
      .then((result: { data: Record<string, unknown> }) => {
        if (!result.data) {
          return;
        }

        // TODO: Test
        // New startFromDate must be applied to both schedules
        this.stepForm.get('incrementalData').get('startFromDate').setValue(result.data.startFromDate);
        this.stepForm.get('fullBackupData').get('startFromDate').setValue(result.data.startFromDate);

        propertiesMap.forEach((key) => {
          this.stepForm.get(key).setValue(result.data[key]);
        });
      })
      .catch(noop);
  }

  protected deactivateSimple() {
    if (this.isSimpleMode) {
      this.activateTile('predefined');

      this.$deactivateSimple.next(true);
    }
  }

  private provideWeeklyDayOfWeek(groupNames: string[]): void {
    groupNames.forEach((name) => {
      const group = this.stepForm.get(name);

      if (group.get('weeklyDayOfWeek').value.some((day) => !!day)) {
        return;
      }

      if (name === 'incrementalData') {
        const template = new UntypedFormGroup(getDefaultIncrementalSchedule());

        return void group.get('weeklyDayOfWeek').reset(template.get('weeklyDayOfWeek').getRawValue());
      }

      const template = new UntypedFormGroup(getDefaultFullSchedule());

      group.get('weeklyDayOfWeek').reset(template.get('weeklyDayOfWeek').getRawValue());
    });
  }

  private checkSchedule(): void {
    const value = this.stepForm.get('ScheduleType').value;
    const scheduleSelected = ['noschedule', 'ffi', 'predefined'].includes(value);

    if (!scheduleSelected) {
      this.invalidScheduleTypeSelection = true;
      this.value = { ...this.value, valid: false };

      return;
    }

    this.invalidScheduleTypeSelection = false;
  }

  private updateScheduleInfo(): void {
    if (this.isSimpleMode) {
      this.scheduleInfo = {
        simple: this.combineScheduleInfo('incrementalData'),
        incremental: '',
        full: ''
      };

      return void this.stepService.updateProperty('schedule', 'scheduleInfo', this.scheduleInfo);
    }

    if (this.isAdvancedMode) {
      this.scheduleInfo = {
        simple: '',
        incremental: this.combineScheduleInfo('incrementalData'),
        full: this.combineScheduleInfo('fullBackupData')
      };

      return void this.stepService.updateProperty('schedule', 'scheduleInfo', this.scheduleInfo);
    }

    this.scheduleInfo = {
      simple: '',
      incremental: '',
      full: ''
    };

    return void this.stepService.updateProperty('schedule', 'scheduleInfo', this.scheduleInfo);
  }

  protected initEvents(): void {
    super.initEvents();

    this.stepForm
      .get('incrementalData')
      .get('enabled')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.stepService.updateProperty('schedule', 'incrementalDataEnabled', value);
      });

    this.stepService
      .pipeProperty('retention', 'isGFSMode')
      .pipe(untilDestroyed(this))
      .subscribe((isGFSMode) => {
        this.isGFSMode = isGFSMode;
      });
  }

  private catchBrokenFFI(): void {
    if (!this.isSimpleMode) {
      return;
    }

    const interact = this.stepService.pipeProperty('wizard', 'userInteractWithSteps');

    this.$deactivateSimple
      .pipe(takeUntil(interact))
      .subscribe((result) => {
        if (result) {
          this.stepService.updateProperty('schedule', 'forceDeactivateFFI', true)
        }
      });
  }

  private combineScheduleInfo(type: 'incrementalData' | 'fullBackupData'): string {
    if (this.notConfiguredFull && type === 'fullBackupData') {
      return this.i18nextService.t('wizards:schedule.advancedMode.fullNotConfigured');
    }

    const value = this.stepForm.get(type).value;

    if (this.isAdvancedMode && type === 'fullBackupData' && !value.enabled) {
      return this.i18nextService.t('wizards:schedule.advancedMode.fullDisabled');
    }

    if (this.isAdvancedMode && type === 'incrementalData' && !value.enabled) {
      return this.i18nextService.t('wizards:schedule.advancedMode.incrementalDisabled');
    }

    const period = value.recurringPeriod;
    const isDaily = period === 'Daily';

    return isDaily ? this.getDailyInfo(value) : this.getMonthlyInfo(value);
  }

  private getDailyInfo(value: IncrementalData | FullBackupData): string {
    const weekDays = value.weeklyDayOfWeek
      .map((value, i) => (value ? this.daysShort[i].toUpperCase() : ''))
      .filter((value) => !!value)
      .join(', ');
    const period = value.recurringPeriod;
    const repeatEvery = value.repeatEveryCount;
    const repeatEveryMeasure = this.i18nextService.t('wizards:schedule.scheduleModal.repeat.weeks');

    return `${period} on: ${weekDays}. ${this.getFreqInfo(value)}. Repeat every: ${repeatEvery} ${repeatEveryMeasure}`;
  }

  private getMonthlyInfo(value: IncrementalData | FullBackupData): string {
    const period = value.recurringPeriod;
    const occurrence = value.occurrence;
    const occurrenceText = (weekNumberEnumArray.find((item) => item.value === occurrence) || { label: '' }).label;
    const isDayOfMonth = occurrence === WeekNumber['Day Of Month'];
    const repeatEvery = value.repeatEveryCount;
    const repeatEveryMeasure = this.i18nextService.t('wizards:schedule.scheduleModal.repeat.months');

    if (isDayOfMonth) {
      const dayOfMonth = value.dayOfMonthCount;

      return `${period} on: ${dayOfMonth} ${String(occurrenceText).toLowerCase()}. ${this.getFreqInfo(
        value
      )}. Repeat every: ${repeatEvery} ${repeatEveryMeasure}`;
    }

    const dayOfWeek = value.dayOfWeek;
    const dayOfWeekText = (daysOfWeekEnumArray.find((item) => item.value === dayOfWeek) || { label: '' }).label;

    return `${period} on: ${String(occurrenceText).toLowerCase()} ${dayOfWeekText}. ${this.getFreqInfo(
      value
    )}. Repeat every: ${repeatEvery} ${repeatEveryMeasure}`;
  }

  private getFreqInfo(value: IncrementalData | FullBackupData): string {
    const isOccursAt = value.dailyFreqPeriodOption === 'OccursAt';
    const occursAtTime = moment(value.occursAtTime, 'HH:mm').format('h:mm\u00A0A');
    const occursEveryCount = (value as IncrementalData).occursEveryCount;
    const occursEveryPeriod = (value as IncrementalData).occursEveryPeriod;
    const occursEveryFromTime = moment((value as IncrementalData).occursEveryFromTime, 'HH:mm').format('h:mm\u00A0A');
    const occursEveryToTime = moment((value as IncrementalData).occursEveryToTime, 'HH:mm').format('h:mm\u00A0A');

    return isOccursAt
      ? `Starts at ${occursAtTime}`
      : `Starts every ${occursEveryCount} ${occursEveryPeriod} from ${occursEveryFromTime} to ${occursEveryToTime}`;
  }

  private isSelectedDaysValidator(formGroup: UntypedFormGroup): ValidationErrors | null {
    return isSelectedDaysValidator(formGroup, this.isNBF, this.isRestore);
  }

  private showGFSAlert(needNextStep: boolean, needSave: boolean): void {
    const modalSettings: ModalSettings = {
      header: { title: this.i18nextService.t('wizards:ffi_warning_title', { format: 'title' }) },
      footer: {
        okButton: { text: this.i18nextService.t('shared:buttons.enable') },
        cancelButton: { text: this.i18nextService.t('shared:buttons.cancel') }
      }
    };

    this.modalService
      .open(modalSettings, this.FFIPayAttentionGFS)
      .then((result) => {
        if (result) {
          this.isFFIConfirmed = true;
          this.stepForm.updateValueAndValidity();

          if (needNextStep || needSave) this.nextStep.emit(needSave);
        }
      })
      .catch(noop);
  }

  private getNeedConfirmFFI(): boolean {
    return (
      !this.isUnix &&
      !this.isCreate &&
      !this.isFFIConfirmed &&
      this.initialScheduleType !== 'ffi' &&
      this.stepForm.get('ScheduleType').value === 'ffi'
    );
  }
}
